import apiClient from "@/api/client";
import type { CashflowEvent } from "@/features/enterprises/types";

const wrap = <T>(res: { data: { data: T } }) => res.data.data;

export interface ExchangeRate {
  id: string;
  currency: string;
  rate: string;
  date: string;
  table_no: string;
  created_at: string;
}

export interface ForeignCurrencyAccount {
  id: string;
  company: string;
  company_code: string;
  currency: string;
  name: string;
  iban: string;
  balance: string;
  balance_date: string;
  notes: string;
  balance_pln: string | null;
  rate: string | null;
  rate_date: string | null;
  created_by: string | null;
  created_by_name: string | null;
  updated_by: string | null;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface Company {
  id: string;
  short_code: string;
  name: string;
  is_active: boolean;
  sort_order: number;
}

export interface LatestBalance {
  company_id: string;
  company_code: string;
  company_name: string;
  date: string | null;
  net_balance: string | null;
  vat_balance: string | null;
  updated_by_name: string | null;
}

export interface CompanyBalance {
  id: string;
  company: string;
  company_code: string;
  date: string;
  net_balance: string;
  vat_balance: string;
  notes: string;
  updated_by: string | null;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface GroupPlannedEntry {
  id: string;
  entry_type: "cost" | "revenue";
  entry_type_display: string;
  name: string;
  amount: string;
  expected_date: string;
  is_recurring: boolean;
  recurrence_months: number | null;
  recurrence_end_date: string | null;
  notes: string;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface NonLiquidAsset {
  id: string;
  name: string;
  value: string;
  as_of_date: string;
  notes: string;
  company: string | null;
  company_code: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface VATOverpaidInvoice {
  invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  project_name: string;
  amount_gross: string;
  amount_net: string;
  vat_credited: string;
  payment_status: "unpaid" | "partially_paid";
  paid_amount: string;
  company_id: string | null;
  company_code: string | null;
}

export interface VATOverpaid {
  total_vat_credited: string;
  invoices: VATOverpaidInvoice[];
}

export interface VATEntry {
  id: string;
  company: string | null;
  company_name: string | null;
  entry_type: "carryforward" | "refund" | "payable" | "cit";
  entry_type_display: string;
  amount: string;
  period: string;
  description: string;
  invoice_ref: string;
  is_paid: boolean;
  paid_date: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface IntercompanyRecurring {
  id: string;
  payer_company: string;
  payer_company_code: string;
  recipient_company: string;
  recipient_company_code: string;
  description: string;
  amount_monthly: string;
  day_of_month: number;
  start_date: string;
  end_date: string | null;
  suspended_from: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface IntercompanyOneTime {
  id: string;
  payer_company: string;
  payer_company_code: string;
  recipient_company: string;
  recipient_company_code: string;
  description: string;
  amount: string;
  cost_date: string;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface BalanceCheckpoint {
  id: string;
  company: string;
  company_code: string;
  recorded_at: string;
  net_amount: string;
  vat_amount: string;
  recorded_by_name: string | null;
}

export interface CITSuggestion {
  period: string;
  net_revenue: string;
  invoice_count: number;
}

export interface VATSuggestion {
  period: string;
  vat_from_invoices: string;
  invoice_count: number;
  current_period: string | null;
  vat_current_month: string;
  current_invoice_count: number;
}

export interface CompanyChoice {
  id: string;
  short_code: string;
  name: string;
}

export const groupFinanceApi = {
  getCompanyChoices: () =>
    apiClient.get<{ data: CompanyChoice[] }>("/group-finance/company-choices/").then(wrap),

  getCompanies: () =>
    apiClient.get<{ data: Company[] }>("/group-finance/companies/").then(wrap),

  getLatestBalances: () =>
    apiClient.get<{ data: LatestBalance[] }>("/group-finance/companies/latest-balances/").then(wrap),

  getBalances: (companyId?: string) =>
    apiClient.get<{ data: CompanyBalance[] }>("/group-finance/balances/", {
      params: companyId ? { company: companyId } : {},
    }).then(wrap),

  createBalance: (data: Omit<CompanyBalance, "id" | "company_code" | "updated_by" | "updated_by_name" | "created_at" | "updated_at">) =>
    apiClient.post<{ data: CompanyBalance }>("/group-finance/balances/", data).then(wrap),

  updateBalance: (id: string, data: Partial<CompanyBalance>) =>
    apiClient.patch<{ data: CompanyBalance }>(`/group-finance/balances/${id}/`, data).then(wrap),

  deleteBalance: (id: string) =>
    apiClient.delete(`/group-finance/balances/${id}/`),

  getPlannedEntries: (type?: "cost" | "revenue") =>
    apiClient.get<{ data: GroupPlannedEntry[] }>("/group-finance/planned-entries/", {
      params: type ? { type } : {},
    }).then(wrap),

  createPlannedEntry: (data: Omit<GroupPlannedEntry, "id" | "entry_type_display" | "created_by" | "created_by_name" | "created_at" | "updated_at">) =>
    apiClient.post<{ data: GroupPlannedEntry }>("/group-finance/planned-entries/", data).then(wrap),

  updatePlannedEntry: (id: string, data: Partial<GroupPlannedEntry>) =>
    apiClient.patch<{ data: GroupPlannedEntry }>(`/group-finance/planned-entries/${id}/`, data).then(wrap),

  deletePlannedEntry: (id: string) =>
    apiClient.delete(`/group-finance/planned-entries/${id}/`),

  getAssets: () =>
    apiClient.get<{ data: NonLiquidAsset[] }>("/group-finance/assets/").then(wrap),

  createAsset: (data: Omit<NonLiquidAsset, "id" | "created_by" | "created_by_name" | "created_at" | "updated_at">) =>
    apiClient.post<{ data: NonLiquidAsset }>("/group-finance/assets/", data).then(wrap),

  updateAsset: (id: string, data: Partial<NonLiquidAsset>) =>
    apiClient.patch<{ data: NonLiquidAsset }>(`/group-finance/assets/${id}/`, data).then(wrap),

  deleteAsset: (id: string) =>
    apiClient.delete(`/group-finance/assets/${id}/`),

  getVATEntries: (type?: string) =>
    apiClient.get<{ data: VATEntry[] }>("/group-finance/vat-entries/", {
      params: type ? { type } : {},
    }).then(wrap),

  getVATOverpaid: () =>
    apiClient.get<{ data: VATOverpaid }>("/group-finance/vat-overpaid/").then(wrap),

  createVATEntry: (data: Omit<VATEntry, "id" | "entry_type_display" | "created_by" | "created_by_name" | "created_at" | "updated_at">) =>
    apiClient.post<{ data: VATEntry }>("/group-finance/vat-entries/", data).then(wrap),

  updateVATEntry: (id: string, data: Partial<VATEntry>) =>
    apiClient.patch<{ data: VATEntry }>(`/group-finance/vat-entries/${id}/`, data).then(wrap),

  deleteVATEntry: (id: string) =>
    apiClient.delete(`/group-finance/vat-entries/${id}/`),

  getCashflow: (companies?: string[]) =>
    apiClient.get<{ data: CashflowEvent[] }>("/group-finance/cashflow/", {
      params: companies?.length ? { companies: companies.join(",") } : {},
    }).then(wrap),

  getIntercompanyRecurring: () =>
    apiClient.get<{ data: IntercompanyRecurring[] }>("/group-finance/intercompany-recurring/").then(wrap),

  createIntercompanyRecurring: (data: Omit<IntercompanyRecurring, "id" | "payer_company_code" | "recipient_company_code" | "created_by" | "created_by_name" | "created_at" | "updated_at">) =>
    apiClient.post<{ data: IntercompanyRecurring }>("/group-finance/intercompany-recurring/", data).then(wrap),

  updateIntercompanyRecurring: (id: string, data: Partial<IntercompanyRecurring>) =>
    apiClient.patch<{ data: IntercompanyRecurring }>(`/group-finance/intercompany-recurring/${id}/`, data).then(wrap),

  deleteIntercompanyRecurring: (id: string) =>
    apiClient.delete(`/group-finance/intercompany-recurring/${id}/`),

  getIntercompanyOneTime: () =>
    apiClient.get<{ data: IntercompanyOneTime[] }>("/group-finance/intercompany-one-time/").then(wrap),

  createIntercompanyOneTime: (data: Omit<IntercompanyOneTime, "id" | "payer_company_code" | "recipient_company_code" | "created_by" | "created_by_name" | "created_at" | "updated_at">) =>
    apiClient.post<{ data: IntercompanyOneTime }>("/group-finance/intercompany-one-time/", data).then(wrap),

  updateIntercompanyOneTime: (id: string, data: Partial<IntercompanyOneTime>) =>
    apiClient.patch<{ data: IntercompanyOneTime }>(`/group-finance/intercompany-one-time/${id}/`, data).then(wrap),

  deleteIntercompanyOneTime: (id: string) =>
    apiClient.delete(`/group-finance/intercompany-one-time/${id}/`),

  getBalanceHistory: (companyId?: string) =>
    apiClient.get<{ data: BalanceCheckpoint[] }>("/group-finance/balance-history/", {
      params: companyId ? { company: companyId } : {},
    }).then(wrap),

  getVATSuggestion: (period: string, company?: string | null) =>
    apiClient.get<{ data: VATSuggestion }>("/group-finance/vat-suggestion/", { params: { period, ...(company ? { company } : {}) } }).then(wrap),

  getCITSuggestion: (period: string) =>
    apiClient.get<{ data: CITSuggestion }>("/group-finance/cit-suggestion/", { params: { period } }).then(wrap),

  getMonthlyPrzRevenue: (companies?: string[]) =>
    apiClient.get<{ data: { issued: Record<string, string>; drafts: Record<string, string> } }>(
      "/group-finance/monthly-prz-revenue/",
      { params: companies?.length ? { companies: companies.join(",") } : {} },
    ).then(wrap),

  getMonthlyExtraCosts: () =>
    apiClient.get<{ data: Record<string, string> }>("/group-finance/monthly-extra-costs/").then(wrap),

  setMonthlyExtraCost: (yearMonth: string, amount: string) =>
    apiClient.put<{ data: Record<string, string> }>(`/group-finance/monthly-extra-costs/${yearMonth}/`, { amount }).then(wrap),

  getOutstandingReceivables: () =>
    apiClient.get<{ data: { total_net: string } }>("/group-finance/outstanding-receivables/").then(wrap),

  getVATCollected: () =>
    apiClient.get<{ data: {
      total: string;
      by_company: {
        company_id: string;
        company_code: string;
        amount: string;
        cutoff_month: string;
        invoices: {
          invoice_id: string;
          invoice_number: string;
          invoice_date: string;
          tax_date: string;
          is_advance_invoice: boolean;
          project_name: string;
          amount_net: string;
          amount_gross: string;
          paid_amount: string;
          payment_basis: "gross" | "net" | "partial" | "none";
          vat_received: string;
          payment_status: string;
          direction: "income" | "cost";
          kind: "client" | "contractor" | "jv";
        }[];
      }[];
      other_costs: {
        vat: string;
        gross_total: string;
        net_total: string;
        count: number;
        cutoff_month: string;
        direction: "cost";
      };
    } }>("/group-finance/vat-collected/").then(wrap),

  getExchangeRates: () =>
    apiClient.get<{ data: ExchangeRate[] }>("/group-finance/exchange-rates/").then(wrap),

  getCurrencyAccounts: (companyId?: string) =>
    apiClient
      .get<{ data: ForeignCurrencyAccount[] }>(
        "/group-finance/currency-accounts/" + (companyId ? `?company=${companyId}` : ""),
      )
      .then(wrap),

  createCurrencyAccount: (data: Omit<ForeignCurrencyAccount, "id" | "company_code" | "balance_pln" | "rate" | "rate_date" | "created_by" | "created_by_name" | "updated_by" | "updated_by_name" | "created_at" | "updated_at">) =>
    apiClient.post<{ data: ForeignCurrencyAccount }>("/group-finance/currency-accounts/", data).then(wrap),

  updateCurrencyAccount: (id: string, data: Partial<ForeignCurrencyAccount>) =>
    apiClient.patch<{ data: ForeignCurrencyAccount }>(`/group-finance/currency-accounts/${id}/`, data).then(wrap),

  deleteCurrencyAccount: (id: string) =>
    apiClient.delete(`/group-finance/currency-accounts/${id}/`),
};
